import { resolveLabel } from "../utils/resolve-label.js";
import { currentText, durationText, remainingText, showDurationText, showElapsedText, showRemainingText, toggleDurationText, toggleElapsedText, unknownText } from "../../../i18n/text/time.js";
import { defaults } from "@videojs/utils/object";
import { getTimeRangeEnd, hasTimeRange } from "@videojs/media";
import { formatTime, formatTimeAsPhrase, secondsToIsoDuration } from "@videojs/utils/time";
//#region src/core/ui/time/core.ts
const TOGGLE_LABELS = {
	current: showElapsedText,
	duration: showDurationText,
	remaining: showRemainingText
};
const DEFAULT_LABELS = {
	current: currentText,
	duration: durationText,
	remaining: remainingText
};
const TOGGLE_DESCRIPTIONS = {
	current: toggleElapsedText,
	duration: toggleDurationText,
	remaining: toggleDurationText
};
var TimeCore = class TimeCore {
	static defaultProps = {
		type: "current",
		negativeSign: "-",
		label: "",
		toggle: false
	};
	#props = { ...TimeCore.defaultProps };
	#media = null;
	#formatLocale;
	constructor(props) {
		if (props) this.setProps(props);
	}
	setProps(props) {
		this.#props = defaults(props, TimeCore.defaultProps);
	}
	setMedia(media) {
		this.#media = media;
	}
	/** @internal Platform adapters set the active i18n locale for digital time formatting. */
	setFormatLocale(locale) {
		this.#formatLocale = locale;
	}
	#getSeconds() {
		const media = this.#media;
		const duration = getTimeRangeEnd(media);
		const { type } = this.#props;
		switch (type) {
			case "current": return media.currentTime;
			case "duration": return duration;
			case "remaining": return media.currentTime - duration;
			default: return 0;
		}
	}
	#getText() {
		const media = this.#media;
		const seconds = this.#getSeconds();
		const duration = getTimeRangeEnd(media);
		const options = this.#formatLocale === void 0 ? void 0 : { locale: this.#formatLocale };
		return formatTime(Math.abs(seconds), duration, options);
	}
	#getPhrase() {
		const { type } = this.#props;
		const seconds = this.#getSeconds();
		if (type === "remaining") return formatTimeAsPhrase(seconds < 0 ? seconds : -Math.abs(seconds));
		return formatTimeAsPhrase(seconds);
	}
	#getDatetime() {
		const seconds = this.#getSeconds();
		return secondsToIsoDuration(Math.abs(seconds));
	}
	#getToggleType(type, currentType) {
		if (type === "current") return currentType === "remaining" ? "current" : "remaining";
		return currentType === "duration" ? "remaining" : "duration";
	}
	getLabel(state, type = this.#props.type) {
		const custom = resolveLabel(this.#props.label, state);
		if (custom !== void 0) return custom;
		if (state.disabled || state.unavailable) return unknownText;
		if (!this.#props.toggle) return DEFAULT_LABELS[this.#props.type];
		const toggleType = this.#getToggleType(type, state.type);
		return TOGGLE_LABELS[toggleType];
	}
	getLabelParams(state) {
		if (resolveLabel(this.#props.label, state) !== void 0 || state.disabled || !this.#props.toggle) return void 0;
		const options = this.#formatLocale === void 0 ? void 0 : { locale: this.#formatLocale };
		const duration = formatTimeAsPhrase(Math.abs(state.seconds), options);
		switch (state.type) {
			case "current": return { duration: `${duration} elapsed` };
			case "duration": return { duration: `${duration} duration` };
			case "remaining": return { duration: `${duration} remaining` };
		}
	}
	getDescription(state, type = this.#props.type) {
		return this.#props.toggle && !state.disabled ? TOGGLE_DESCRIPTIONS[type] : void 0;
	}
	getAttrs(state, type = this.#props.type) {
		return {
			"aria-label": this.getLabel(state, type),
			"aria-description": this.getDescription(state, type),
			"aria-disabled": this.#props.toggle && state.disabled ? "true" : void 0,
			role: this.#props.toggle ? "button" : void 0,
			tabIndex: this.#props.toggle ? state.disabled ? -1 : 0 : void 0
		};
	}
	getState() {
		const seconds = this.#getSeconds();
		const unavailable = !hasTimeRange(this.#media);
		return {
			type: this.#props.type,
			disabled: this.#props.toggle && unavailable,
			unavailable: !this.#props.toggle && unavailable,
			seconds,
			negative: this.#props.type === "remaining" && seconds < 0,
			text: this.#getText(),
			phrase: this.#getPhrase(),
			datetime: this.#getDatetime()
		};
	}
};
//#endregion
export { TimeCore };

//# sourceMappingURL=core.js.map